import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { useBudgetVsActual } from '../../hooks/useBudgets';
import { useActiveCompanyId } from '../../stores/useCompanyStore';
import { formatCurrency } from '../../lib/formatters';

export const Route = createFileRoute('/_auth/reports/budget-vs-actual')({
  component: BudgetVsActualPage,
});

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function varianceCell(variance: string, favorable: boolean) {
  return (
    <td style={{ textAlign: 'right', color: favorable ? 'var(--ok)' : 'var(--bad)', fontWeight: 600 }}>
      {formatCurrency(variance)}
    </td>
  );
}

function BudgetVsActualPage() {
  const companyId = useActiveCompanyId();
  const thisYear = new Date().getFullYear();
  const [year, setYear] = useState(thisYear);
  const [fromMonth, setFromMonth] = useState(1);
  const [toMonth, setToMonth] = useState(12);
  const { data, isLoading, error } = useBudgetVsActual(companyId, year, fromMonth, toMonth);

  if (!companyId) {
    return (
      <div className="card" style={{ textAlign: 'center', padding: 32 }}>
        <h1 style={{ marginTop: 0 }}>Budget vs. Actual</h1>
        <p className="muted">No active company. This is a per-company report.</p>
      </div>
    );
  }

  const rows = data?.rows ?? [];
  const t = data?.totals;

  return (
    <div>
      <Link to="/reports" style={{ fontSize: 13 }}>← Reports</Link>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '8px 0 16px', flexWrap: 'wrap', gap: 12 }}>
        <h1 style={{ fontSize: 24, margin: 0 }}>Budget vs. Actual</h1>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', fontSize: 13 }}>
          <label>Year{' '}
            <select value={year} onChange={(e) => setYear(Number(e.target.value))}>
              {[thisYear - 1, thisYear, thisYear + 1].map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </label>
          <label>From{' '}
            <select value={fromMonth} onChange={(e) => setFromMonth(Number(e.target.value))}>
              {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
            </select>
          </label>
          <label>To{' '}
            <select value={toMonth} onChange={(e) => setToMonth(Number(e.target.value))}>
              {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
            </select>
          </label>
        </div>
      </div>

      {isLoading && <p className="muted">Loading…</p>}
      {error && <p className="field-error">Failed to load report.</p>}

      {t && (
        <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
          <div className="card" style={{ padding: 16, minWidth: 200 }}>
            <div className="muted" style={{ fontSize: 12 }}>Net income — budget</div>
            <div style={{ fontSize: 20, fontWeight: 700 }}>{formatCurrency(t.netBudget)}</div>
          </div>
          <div className="card" style={{ padding: 16, minWidth: 200 }}>
            <div className="muted" style={{ fontSize: 12 }}>Net income — actual</div>
            <div style={{ fontSize: 20, fontWeight: 700 }}>{formatCurrency(t.netActual)}</div>
          </div>
          <div className="card" style={{ padding: 16, minWidth: 200 }}>
            <div className="muted" style={{ fontSize: 12 }}>Variance ({t.netFavorable ? 'favorable' : 'unfavorable'})</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: t.netFavorable ? 'var(--ok)' : 'var(--bad)' }}>
              {formatCurrency(t.netVariance)}
            </div>
          </div>
        </div>
      )}

      {!isLoading && rows.length === 0 && (
        <div className="card" style={{ textAlign: 'center', padding: 32 }}>
          <p className="muted" style={{ marginTop: 0 }}>No budget or activity for this period.</p>
          <Link to="/budget" className="btn-primary" style={{ display: 'inline-block', textDecoration: 'none' }}>Set up a budget</Link>
        </div>
      )}

      {rows.length > 0 && (
        <table className="table">
          <thead>
            <tr>
              <th>Account</th>
              <th>Type</th>
              <th style={{ textAlign: 'right' }}>Budget</th>
              <th style={{ textAlign: 'right' }}>Actual</th>
              <th style={{ textAlign: 'right' }}>Variance</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.accountId}>
                <td style={{ fontWeight: 500 }}>{r.code} — {r.name}</td>
                <td className="muted">{r.accountType.replace(/_/g, ' ')}</td>
                <td style={{ textAlign: 'right' }}>{formatCurrency(r.budget)}</td>
                <td style={{ textAlign: 'right' }}>{formatCurrency(r.actual)}</td>
                {varianceCell(r.variance, r.favorable)}
              </tr>
            ))}
          </tbody>
          {t && (
            <tfoot>
              <tr style={{ fontWeight: 700, borderTop: '2px solid var(--border)' }}>
                <td colSpan={2}>Net income</td>
                <td style={{ textAlign: 'right' }}>{formatCurrency(t.netBudget)}</td>
                <td style={{ textAlign: 'right' }}>{formatCurrency(t.netActual)}</td>
                {varianceCell(t.netVariance, t.netFavorable)}
              </tr>
            </tfoot>
          )}
        </table>
      )}
    </div>
  );
}
